'use client'

import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OperatorData, OperatorGroup } from '@/lib/pbx/flow/types'
import { useOperatorGroups } from '@/hooks/pbx/use-operator-groups'

interface OperatorPanelProps {
  data: OperatorData
  onUpdate: (data: Partial<OperatorData>) => void
}

export function OperatorPanel({ data, onUpdate }: OperatorPanelProps) {
  const { groups } = useOperatorGroups()
  const [newNumber, setNewNumber] = useState('')

  const selectedGroup = data.operator_group_id
    ? groups.find((g: OperatorGroup) => g.id === data.operator_group_id)
    : null

  const addPhoneNumber = () => {
    if (newNumber && !data.phone_numbers?.includes(newNumber)) {
      onUpdate({
        phone_numbers: [...(data.phone_numbers || []), newNumber],
      })
      setNewNumber('')
    }
  }

  const removePhoneNumber = (number: string) => {
    onUpdate({
      phone_numbers: (data.phone_numbers || []).filter((n) => n !== number),
    })
  }

  const handleGroupSelect = (value: string) => {
    if (value === 'none') {
      // Switching to direct numbers - set default values
      onUpdate({
        operator_group_id: undefined,
        timeout: 30,
        simulring: true,
      })
    } else {
      // Switching to group - clear node-level settings (will use group settings)
      onUpdate({
        operator_group_id: value,
        phone_numbers: [],
        timeout: undefined,
        simulring: undefined,
      })
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label>Operator Group</Label>
        <Select
          value={data.operator_group_id || 'none'}
          onValueChange={handleGroupSelect}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No group (use direct numbers)</SelectItem>
            {groups.map((group: OperatorGroup) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name} <span>(</span>{group.phone_numbers.length} <span>numbers)</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Group selected - show group info */}
      {selectedGroup && (
        <div className="p-3 bg-muted rounded-lg space-y-2">
          <p className="text-sm font-medium">{selectedGroup.name}</p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p><span>Numbers:</span> {selectedGroup.phone_numbers.length}</p>
            <p><span>Timeout:</span> {selectedGroup.timeout || 30}<span>s</span></p>
            <p><span>Strategy:</span> <span>{selectedGroup.ring_strategy === 'simulring' ? 'Ring all simultaneously' : 'Sequential'}</span></p>
          </div>
          <p className="text-xs text-muted-foreground italic">
            Settings are managed in the operator group. Edit the group to change them.
          </p>
        </div>
      )}

      {/* No group - show direct number input and settings */}
      {!data.operator_group_id && (
        <>
          <div className="space-y-2">
            <Label>Phone Numbers</Label>
            <div className="flex gap-2">
              <Input
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                placeholder="+1XXXXXXXXXX"
              />
              <Button size="icon" onClick={addPhoneNumber}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-1">
              {(data.phone_numbers || []).map((number) => (
                <div
                  key={number}
                  className="flex items-center justify-between p-2 bg-muted rounded"
                >
                  <span className="text-sm font-mono">{number}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePhoneNumber(number)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timeout">Timeout (seconds)</Label>
            <Input
              id="timeout"
              type="number"
              min={5}
              max={300}
              value={data.timeout || 30}
              onChange={(e) => onUpdate({ timeout: parseInt(e.target.value, 10) })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Ring All Simultaneously</Label>
              <p className="text-xs text-muted-foreground">
                Ring all numbers at once vs sequential
              </p>
            </div>
            <Switch
              checked={data.simulring ?? true}
              onCheckedChange={(checked) => onUpdate({ simulring: checked })}
            />
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label>Caller ID Display</Label>
        <Select
          value={data.caller_id_mode || 'customer'}
          onValueChange={(value: 'platform' | 'customer') =>
            onUpdate({ caller_id_mode: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="customer">Show caller&apos;s number</SelectItem>
            <SelectItem value="platform">Show platform number</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Recording Settings */}
      <div className="pt-4 border-t space-y-4">
        <Label className="text-sm font-medium">Recording</Label>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Record Operator Calls</Label>
            <p className="text-xs text-muted-foreground">
              Save recordings for quality assurance
            </p>
          </div>
          <Switch
            checked={data.record_call ?? false}
            onCheckedChange={(checked) => onUpdate({ record_call: checked })}
          />
        </div>
      </div>
    </div>
  )
}
